import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import numOrDefault from 'lib/helpers/numOrDefault';
import { cn, ia } from 'lib/helpers/utility';
import { useInvoice } from 'lib/hooks/queries/public/useInvoice';

import NotFound from 'components/NotFound';
import {
  PracticeInformationDesktop,
  PracticeInformationMobile
} from 'components/practice/BeyondBilling/ElectronicInvoicing/components/Invoice/components/InvoiceHeader/PracticeInformation';

import { getProceduresTotal } from '../lib/utils';

import InvoiceWithTokenSkeleton from './InvoiceWithTokenSkeleton';
import SuccessPayment from './SuccessPayment';
import BalanceSummary from './components/BalanceSummary';
import InvoiceDetails from './components/InvoiceDetails/InvoiceDetails';
import MemoAndInfo from './components/MemoAndInfo';
import PatientInformation from './components/PatientInformation';
import SecurePayment from './components/SecurePayment/SecurePayment';

const InvoiceWithToken = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [transactionId, setTransactionId] = useState(null);

  const { data, isLoading } = useInvoice({
    params: { navigate, token },
    dependencies: [token]
  });

  const externalInvoice = data?.invoice;

  const isAEOB = externalInvoice?.aeob;
  const type = externalInvoice?.type;
  const invoices = externalInvoice?.internal_invoices || [];

  const isKiosk = type === 'kiosk';

  let patientInvoice = {
    discount: externalInvoice?.discount_amount_cents,
    tax: externalInvoice?.tax_percentage,
    tax_amount_cents: externalInvoice?.tax_amount_cents,
    surcharge_amount: numOrDefault(externalInvoice?.surcharge_amount)
  };

  if (ia(invoices)) {
    const procedureTotalData = getProceduresTotal(invoices);
    patientInvoice = { ...patientInvoice, ...procedureTotalData };
  }

  const {
    tax_amount_cents = 0,
    discount = 0,
    prevDiscount = 0,
    charge = 0,
    amount_paid = 0,
    ins_payment = 0,
    ins_adjustment = 0,
    surcharge_amount = 0,
    ptBalance = 0,
    other_adj = 0,
    write_off = 0
  } = patientInvoice || {};

  let total =
    charge -
    amount_paid -
    ins_payment -
    ins_adjustment -
    prevDiscount +
    tax_amount_cents -
    write_off;

  if (!isAEOB) total = other_adj > 0 ? charge - other_adj : ptBalance;
  total = ptBalance;

  if (isKiosk) total = externalInvoice?.total_amount_cents;

  const amountDue = !isAEOB
    ? total - discount + (!isKiosk ? surcharge_amount : 0)
    : total - discount + (!isKiosk ? surcharge_amount : 0) - other_adj;

  const practice = externalInvoice?.practice;

  if (!data?.invoice && !isLoading)
    return (
      <div className="h-dvh w-dvw overflow-hidden p-10">
        <NotFound />
      </div>
    );
  const onSuccess = (payload) => {
    const transactionId =
      typeof payload === 'string' || Number.isInteger(payload) ? payload : payload?.transactionId;
    setTransactionId(transactionId);
  };

  if (isLoading) {
    return <InvoiceWithTokenSkeleton />;
  }

  if (transactionId) {
    return <SuccessPayment transactionId={transactionId} />;
  }

  return (
    <div className="h-dvh w-dvw overflow-hidden bg-[linear-gradient(180deg,#F0F9FF_0%,#B9E6FE_100%)] p-10">
      <div className="relative flex h-full flex-col rounded-xl bg-white shadow-[0px_0px_8px_rgba(65,70,81,0.1)]">
        <div className={cn('flex flex-1 flex-col overflow-y-auto', amountDue > 0 && 'mb-20')}>
          <PracticeInformationDesktop
            className="rounded-t-xl"
            practice={externalInvoice?.practice}
            title={externalInvoice?.title}
          />
          <PracticeInformationMobile
            className="min-h-fit rounded-t-xl"
            practice={externalInvoice?.practice}
            title={externalInvoice?.title}
          />
          <PatientInformation
            patient={externalInvoice?.patient}
            created_at={externalInvoice?.created_at}
            dueDate={externalInvoice?.due_date}
            invoiceId={externalInvoice?.id}
          />
          <div className="flex flex-col">
            <BalanceSummary
              externalInvoice={externalInvoice}
              invoices={invoices}
              amountDue={amountDue}
            />
            <InvoiceDetails invoices={invoices} invoice={externalInvoice} />
            <MemoAndInfo memo={externalInvoice?.memo} />
          </div>
        </div>

        {amountDue > 0 && (
          <SecurePayment
            amountDue={amountDue}
            practice={practice}
            patient={externalInvoice?.patient}
            invoices={invoices}
            token={token}
            onSuccess={onSuccess}
            invoice={externalInvoice}
          />
        )}
      </div>
    </div>
  );
};

export default InvoiceWithToken;
